import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Form, Input, Button, Alert, Typography, Card, Space } from 'antd';
import { MailOutlined, LockOutlined, KeyOutlined } from '@ant-design/icons';
import { authService } from '../../services/auth.service';
import { getUserFriendlyMessage } from '../../utils/error-handler';
import {
  PASSWORD_POLICY_HINT,
  PASSWORD_POLICY_MESSAGE,
  PASSWORD_POLICY_REGEX,
} from '../../utils/password-policy';

const { Title, Paragraph, Text } = Typography;

interface ForgotPasswordFormProps {
  initialEmail?: string;
}

interface RequestFormValues {
  email: string;
}

interface ConfirmFormValues {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

export const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({
  initialEmail,
}) => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState(initialEmail || '');
  const navigate = useNavigate();
  const [requestForm] = Form.useForm<RequestFormValues>();
  const [confirmForm] = Form.useForm<ConfirmFormValues>();

  const handleRequestCode = async (values: RequestFormValues) => {
    setApiError(null);
    setLoading(true);

    try {
      await authService.forgotPassword(values.email);
      setEmail(values.email);
      setStep('confirm');
    } catch (err: any) {
      const status = err?.response?.status;
      const action = err?.response?.data?.action;

      if (status === 403 && action === 'CONFIRM_ACCOUNT') {
        navigate('/confirm-account', {
          state: {
            email: values.email,
          },
        });
        return;
      }

      const errorMessage = getUserFriendlyMessage(err);
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPassword = async (values: ConfirmFormValues) => {
    setApiError(null);
    setLoading(true);

    try {
      await authService.confirmPassword(email, values.code, values.newPassword);
      navigate('/login', {
        state: {
          message: 'Senha alterada com sucesso! Faça login para continuar.',
        },
      });
    } catch (err: any) {
      const errorMessage = getUserFriendlyMessage(err);
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 500, margin: '80px auto', padding: '0 20px' }}>
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Title level={2} style={{ textAlign: 'center', marginBottom: 0 }}>
            Recuperar Senha
          </Title>

          <Paragraph type="secondary" style={{ textAlign: 'center', marginTop: 0 }}>
            {step === 'request'
              ? 'Enviaremos um código de verificação para seu email'
              : 'Digite o código e crie uma nova senha'}
          </Paragraph>

          {apiError && (
            <Alert
              message="Erro"
              description={apiError}
              type="error"
              showIcon
              closable
              onClose={() => setApiError(null)}
            />
          )}

          {step === 'request' && (
            <Form
              form={requestForm}
              name="forgot-password-request"
              onFinish={handleRequestCode}
              layout="vertical"
              autoComplete="off"
              initialValues={{ email }}
            >
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Por favor, insira seu email!' },
                  { type: 'email', message: 'Email inválido!' },
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="seu@email.com"
                  size="large"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  block
                >
                  Enviar código
                </Button>
              </Form.Item>
            </Form>
          )}

          {step === 'confirm' && (
            <Form
              form={confirmForm}
              name="forgot-password-confirm"
              onFinish={handleConfirmPassword}
              layout="vertical"
              autoComplete="off"
            >
              <Form.Item
                label="Código de verificação"
                name="code"
                rules={[
                  { required: true, message: 'Por favor, insira o código!' },
                ]}
              >
                <Input
                  prefix={<KeyOutlined />}
                  placeholder="000000"
                  size="large"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                label="Nova senha"
                name="newPassword"
                rules={[
                  { required: true, message: 'Por favor, insira sua nova senha!' },
                  { min: 8, message: 'Senha deve ter no mínimo 8 caracteres!' },
                  { pattern: PASSWORD_POLICY_REGEX, message: PASSWORD_POLICY_MESSAGE },
                ]}
                extra={PASSWORD_POLICY_HINT}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="••••••••"
                  size="large"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item
                label="Confirmar nova senha"
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Por favor, confirme sua senha!' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('As senhas não coincidem!'));
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="••••••••"
                  size="large"
                  disabled={loading}
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  size="large"
                  block
                >
                  Alterar senha
                </Button>
              </Form.Item>
            </Form>
          )}

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">
              Lembrou sua senha? <Link to="/login">Entrar</Link>
            </Text>
          </div>
        </Space>
      </Card>
    </div>
  );
};
